import { useMemo, useState } from 'react';

import { Card } from 'react-bootstrap';
import { faCircleArrowDown } from '@fortawesome/free-solid-svg-icons';

import IconButton from '@components/Button/IconButton';
import InfoDialog from '@components/Modals/InfoDialog';
import { ExtendedOrder, OrderStatus } from '@customTypes/order';
import { getApoeResultResources } from '@helpers/apoeResultResources';
import { formatDateForDisplay } from '@helpers/date';
import { getOrderTestDisplayLabels } from '@helpers/patient';
import { useDownload } from '@hooks/useDownload';
import { IS_NATERA } from '@constants/constants';

interface IProps {
  order: ExtendedOrder;
  requisitionId: string;
}

export default function PatientDetailOrderCard({ order, requisitionId }: IProps) {
  const { onDownload } = useDownload();
  const [showNoPdfModal, setShowNoPdfModal] = useState(false);

  const reportedColorVar = IS_NATERA ? 'accent-subtle-1' : 'primary';
  const reportedBorderVar = IS_NATERA ? undefined : 'primary';

  const colorVariant = order.isReported ? reportedColorVar : 'light';
  const borderVariant = order.isReported ? reportedBorderVar : undefined;
  const isCanceled = order.status === OrderStatus.CANCELED;
  const canceledOnDisplay = useMemo(() => {
    if (!isCanceled || !order.canceled_at) {
      return undefined;
    }
    return formatDateForDisplay(order.canceled_at);
  }, [isCanceled, order.canceled_at]);

  const displayLabels = useMemo(() => getOrderTestDisplayLabels(order), [order]);
  const apoeResources = order.isReported ? getApoeResultResources(order) : [];

  return (
    <>
      <Card
        className={`patient-detail-order-card ${isCanceled ? 'canceled' : ''} mx-1`}
        border={borderVariant}
        bg={colorVariant}
      >
        {isCanceled && (
          <Card.Header className='text-body justify-content-center order-test-status'>Canceled</Card.Header>
        )}
        <Card.Body
          className={`d-flex flex-column align-items-center gap-2 ${isCanceled ? 'bg-body-tertiary' : 'bg-body'}`}
        >
          <div className='d-flex flex-column align-items-center order-test-name'>
            {displayLabels.showHeadline && (
              <p className='fs-5 lh-xs text-center fw-semibold'>{displayLabels.headline}</p>
            )}

            {!!displayLabels.subline && (
              <p className='lh-xs fw-semibold mt-1 text-body-secondary'>{displayLabels.subline}</p>
            )}
          </div>

          {order.reported_at && order.isReported && (
            <p className='mb-0'>Report date: {formatDateForDisplay(order.reported_at)}</p>
          )}

          {!order.isReported && order.created_at && !(isCanceled && canceledOnDisplay) && (
            <p className='mb-0'>Order date: {formatDateForDisplay(order.created_at)}</p>
          )}

          {canceledOnDisplay && <p className='mb-0'>Canceled on: {canceledOnDisplay}</p>}

          {(order.isReported || isCanceled) && (
            <IconButton
              startIcon={faCircleArrowDown}
              size='sm'
              variant='primary'
              className='mt-auto mx-3 w-100 justify-content-center'
              onClick={() =>
                onDownload(
                  order,
                  requisitionId,
                  isCanceled ? { onNoReportPdf: () => setShowNoPdfModal(true) } : undefined,
                )
              }
            >
              {isCanceled ? 'Test Not Performed Report' : 'Results'}
            </IconButton>
          )}

          {apoeResources.length > 0 && (
            <ul className='list-unstyled d-flex flex-column align-items-center gap-1 mb-0 w-100'>
              {apoeResources.map(resource => (
                <li key={resource.url}>
                  <a href={resource.url} target='_blank' rel='noopener noreferrer' className='fs-xs text-center'>
                    {resource.title}
                  </a>
                </li>
              ))}
            </ul>
          )}
        </Card.Body>
      </Card>

      {showNoPdfModal && (
        <InfoDialog title='No report available' onClose={() => setShowNoPdfModal(false)}>
          <p className='mb-0 text-center'>No report PDF available for this order.</p>
        </InfoDialog>
      )}
    </>
  );
}
